import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PrepCard from "@/pages/prep/PrepCard";
import WeakAreaNudge from "@/pages/prep/WeakAreaNudge";
import api, { API } from "@/lib/api";
import {
  Compass, Flame, Zap, Award, MessageSquare, Target, TrendingUp, Users,
  Calendar, ArrowRight, Sparkles, ClipboardCheck, Video, Download, Loader2, GraduationCap,
  CheckCircle2, Circle, XCircle, Clock,
} from "lucide-react";

const QUICK = [
  { to: "/chat", label: "Ask AI Coach", icon: MessageSquare, color: "cyan" },
  { to: "/predictor/college", label: "Predict Colleges", icon: Target, color: "indigo" },
  { to: "/predictor/rank", label: "Predict Rank", icon: TrendingUp, color: "emerald" },
  { to: "/counsellors", label: "Book Mentor", icon: Users, color: "amber" },
];
const cmap = {
  cyan: "text-cyan-300 border-cyan-400/20 bg-cyan-400/5",
  indigo: "text-indigo-300 border-indigo-400/20 bg-indigo-400/5",
  emerald: "text-emerald-300 border-emerald-400/20 bg-emerald-400/5",
  amber: "text-amber-300 border-amber-400/20 bg-amber-400/5",
};

export default function StudentDashboard() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get("/student/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const downloadBlueprint = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("pf_token");
      const res = await fetch(`${API}/report/blueprint`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Complete your assessment first.");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "EntranceZone_Career_Blueprint.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Career Blueprint downloaded!");
    } catch {
      toast.error("Download failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Welcome back</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight">Hi, {data.name.split(" ")[0]} 👋</h1>
        </div>
        {data.assessment_completed && (
          <button data-testid="download-blueprint-btn" onClick={downloadBlueprint} disabled={downloading}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-5 py-2.5 text-sm font-semibold text-cyan-300 transition-all hover:bg-cyan-400/15 disabled:opacity-60">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Career Blueprint PDF
          </button>
        )}
      </motion.div>

      {!data.assessment_completed && (
        <Link to="/assessment" data-testid="dash-assessment-cta"
          className="mt-6 flex items-center justify-between rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-[#0F2A33] to-[#131C2E] p-5 transition-transform hover:scale-[1.005]">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400/15">
              <ClipboardCheck className="h-6 w-6 text-cyan-400" />
            </div>
            <div>
              <p className="font-semibold text-white">Complete your AI assessment</p>
              <p className="text-sm text-slate-400">Unlock your personalized career profile (+100 XP)</p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-cyan-400" />
        </Link>
      )}

      <PrepCard />
      <WeakAreaNudge />

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Compass} label="Career Clarity" value={`${data.clarity_score}%`} color="cyan" testid="student-dashboard-clarity-meter" />
        <Stat icon={Zap} label="Total XP" value={data.xp} color="indigo" />
        <Stat icon={Flame} label="Day Streak" value={data.streak} color="amber" testid="student-streak-badge" />
        <Stat icon={Award} label="Badges" value={(data.badges || []).length} color="emerald" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-6">
          <ParentAccessRequests />
          <Link to="/apply-colleges" data-testid="dash-apply-colleges-cta"
            className="flex items-center justify-between rounded-2xl border border-cyan-400/25 bg-gradient-to-r from-[#0F2A33] to-[#131C2E] p-5 transition-transform hover:scale-[1.005]">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-400/15">
                <GraduationCap className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <p className="font-semibold text-white">Recommended colleges to apply</p>
                <p className="text-sm text-slate-400">AI-matched to your profile — apply in minutes with mobile OTP</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-cyan-400" />
          </Link>
          <ApplicationsTimeline />
          {/* Quick actions */}
          <div>
            <h3 className="font-display text-lg font-semibold mb-3">Quick actions</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {QUICK.map((q) => {
                const Icon = q.icon;
                return (
                  <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center gap-3 rounded-xl border border-white/8 bg-[#131C2E] p-4 transition-all hover:border-cyan-400/30 hover:-translate-y-0.5">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${cmap[q.color]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="font-medium text-white">{q.label}</span>
                    <ArrowRight className="ml-auto h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-1" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Career matches */}
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-cyan-400" /> Your top career matches
            </h3>
            {(data.recommended_careers || []).length === 0 ? (
              <p className="text-sm text-slate-400">Complete your assessment to see AI career matches.</p>
            ) : (
              <div className="space-y-4">
                {data.recommended_careers.map((c) => (
                  <div key={c.title}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-white">{c.title}</span>
                      <span className="font-mono-data text-cyan-300">{c.match}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                      <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-400"
                        initial={{ width: 0 }} animate={{ width: `${c.match}%` }} transition={{ duration: 0.8 }} />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{c.why}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right col */}
        <div className="space-y-6">
          {/* Sessions */}
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-400" /> Upcoming sessions
            </h3>
            {(data.upcoming_sessions || []).length === 0 ? (
              <div className="text-center py-4">
                <p className="text-sm text-slate-400 mb-3">No sessions booked yet.</p>
                <Link to="/counsellors" className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-400">
                  Find a counsellor <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {data.upcoming_sessions.slice(0, 3).map((s) => (
                  <div key={s.id} data-testid="student-session-item" className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-white">{s.counsellor_name}</p>
                      <span className="rounded-full bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">{s.status}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{s.date} · {s.slot} · {s.topic}</p>
                    <a href={s.meet_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-400">
                      <Video className="h-3 w-3" /> Join call
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-400" /> Achievements
            </h3>
            <div className="flex flex-wrap gap-2">
              {(data.badges || []).length === 0 && <p className="text-sm text-slate-400">Earn badges as you explore!</p>}
              {(data.badges || []).map((b) => (
                <span key={b} className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 border border-amber-400/20 px-3 py-1.5 text-xs font-semibold text-amber-300">
                  <Sparkles className="h-3 w-3" /> {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const APP_STEPS = ["Applied", "Under Review", "Shortlisted", "Accepted"];

function ApplicationsTimeline() {
  const [apps, setApps] = useState([]);
  useEffect(() => { api.get("/colleges/recommended/applications/me").then((r) => setApps(r.data)).catch(() => {}); }, []);
  if (apps.length === 0) return null;
  return (
    <div data-testid="applications-timeline" className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
      <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
        <GraduationCap className="h-5 w-5 text-cyan-400" /> My college applications
      </h3>
      <div className="space-y-6">
        {apps.map((a) => {
          const rejected = a.status === "Rejected";
          const waitlisted = a.status === "Waitlisted";
          const terminal = rejected || waitlisted;
          const rawIdx = terminal ? APP_STEPS.length - 1 : APP_STEPS.indexOf(a.status);
          const curIdx = rawIdx === -1 ? 0 : rawIdx;
          const histMap = {};
          (a.status_history || []).forEach((h) => { histMap[h.status] = h.at; });
          const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";
          const steps = terminal ? [...APP_STEPS.slice(0, 3), a.status] : APP_STEPS;
          return (
            <div key={a.id} data-testid="application-timeline-item" className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{a.college_name}</p>
                  <p className="text-xs text-slate-400 truncate">{a.program}</p>
                </div>
                <span data-testid="application-current-status" className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${
                  rejected ? "bg-rose-400/15 text-rose-300" : a.status === "Accepted" ? "bg-emerald-400/15 text-emerald-300"
                  : waitlisted ? "bg-amber-400/15 text-amber-300" : "bg-cyan-400/15 text-cyan-300"}`}>
                  {a.status}
                </span>
              </div>
              <div className="flex items-start">
                {steps.map((s, i) => {
                  const isTerminalNode = terminal && i === steps.length - 1;
                  const done = i < curIdx || (i === curIdx && (a.status === "Accepted" || isTerminalNode));
                  const active = i === curIdx;
                  const Icon = isTerminalNode && rejected ? XCircle : done ? CheckCircle2 : active ? Clock : Circle;
                  const color = isTerminalNode && rejected ? "text-rose-400" : isTerminalNode && waitlisted ? "text-amber-400"
                    : done || active ? "text-cyan-400" : "text-slate-600";
                  return (
                    <div key={s} className="flex items-start" style={{ flex: i < steps.length - 1 ? 1 : "0 0 auto" }}>
                      <div className="flex flex-col items-center gap-1 w-16">
                        <Icon className={`h-5 w-5 ${color}`} />
                        <span className={`text-[10px] font-medium text-center leading-tight h-7 flex items-center ${done || active ? "text-slate-200" : "text-slate-500"}`}>{s}</span>
                        <span className="text-[9px] text-slate-500 h-3" data-testid="timeline-step-date">{i <= curIdx ? fmtDate(histMap[s]) : ""}</span>
                      </div>
                      {i < steps.length - 1 && (
                        <div className={`h-0.5 flex-1 mx-1 mt-2.5 ${i < curIdx ? "bg-cyan-400" : "bg-white/10"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              {a.whatsapp_optin && (
                <p className="mt-3 text-[10px] text-emerald-400/80 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> WhatsApp updates on
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={testid}
    className="rounded-2xl border border-white/8 bg-gradient-to-br from-[#131C2E] to-[#0F1626] p-5">
    <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl border ${cmap[color]}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="font-display text-2xl font-extrabold text-white">{value}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </motion.div>
);

function ParentAccessRequests() {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/student/parent-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const respond = async (id, approve) => {
    try {
      await api.post(`/student/parent-requests/${id}/respond`, { approve });
      toast.success(approve ? "Parent access approved" : "Request declined");
      load();
    } catch { toast.error("Action failed"); }
  };
  const pending = reqs.filter((r) => r.status === "pending");
  if (pending.length === 0) return null;
  return (
    <div data-testid="parent-access-requests" className="rounded-2xl border border-amber-400/25 bg-amber-400/5 p-5">
      <p className="text-sm font-semibold text-amber-300 mb-3">Parent access requests</p>
      {pending.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] p-3">
          <p className="text-sm text-white"><span className="font-semibold">{r.parent_name}</span> wants to view your career profile</p>
          <div className="flex gap-2">
            <button data-testid="approve-parent-btn" onClick={() => respond(r.id, true)}
              className="rounded-full bg-emerald-400 px-3 py-1.5 text-xs font-bold text-[#041016]">Approve</button>
            <button data-testid="decline-parent-btn" onClick={() => respond(r.id, false)}
              className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-300">Decline</button>
          </div>
        </div>
      ))}
      <p className="mt-2 text-[11px] text-slate-500">Your private AI chats are never shared. You control this access anytime.</p>
    </div>
  );
}